import Checkbox from "components/Input/Checkbox";
import { getEventDefaultCountryCode } from "lib/eventsApi";
import { useEffect, useRef, useState } from "react";
import ROUTES from "routes";
import PageLayout from "templates/PageLayout";
import usePreviousRoute from "utils/usePreviousRoute";
import { FlightModel } from "./ModelTools";
import { useParams } from "react-router-dom";
import useEventRouting from "hooks/useEventRouting";
import { useUser } from "state/UserContext";
import { useQuery } from "@tanstack/react-query";
import { getAttendees } from "lib/userApi";
import { getFlightById } from "lib/flightsApi";
import toast from "react-hot-toast";

import Stack from "components/Stack";
import { FormSection } from "components/Containers";
import { FileInput, Input } from "components/Input";

import { getAvatar, getFlightType } from "Models/User";
import HR from "components/HR";
import SelectGuest from "./components/SelectGuest";
import Label from "components/Label";
import { Button } from "components/Button";
import FlightInfoSection from "./components/FlightInfoSection";
import { useForm } from "react-hook-form";
import {
  FlightFormOutput,
  FlightFormSchemaType,
  getFlightFormSchema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/FormV2/FormV2";
import openToast from "utils/openToast";
import { createFlight, updateFlight } from "lib/tripsApi";
import { getLatLngByAddress } from "components/AutoCompletePlaces/AutoCompletePlaces";
import { useTranslation } from "react-i18next";
import FEATURE_FLAGS from "state/featureFlags/flags";
import { useFeatureFlag } from "state/featureFlags";
import useExtractFlightAi from "./useExtractFlightAI";
import AIUploadComponent from "components/Input/AIUploadComponent";
import uploadCloudinary from "utils/uploadCloudinary";
import classNames from "classnames";
import { FlightFieldsToExtract } from "lib/ai/flightAI";

export const SERVER_OUTBOUND_LOCATION_LAT_LNG_FIELDS = [
  "outbound_departure_location_lat",
  "outbound_departure_location_lng",
  "outbound_arrival_location_lat",
  "outbound_arrival_location_lng",
];

export const SERVER_RETURN_LOCATION_LAT_LNG_FIELDS = [
  "return_departure_location_lat",
  "return_departure_location_lng",
  "return_arrival_location_lat",
  "return_arrival_location_lng",
];

type TicketState = {
  state: "initial" | "success";
  url: string;
  isLoading: boolean;
  name: string;
};

const INITIAL_TICKET_STATE = {
  state: "initial",
  url: "",
  isLoading: false,
  name: "",
} satisfies TicketState;

const applyLocationsLatLngToBody = (
  fields: string[],
  {
    departureLatLng,
    arrivalLatLng,
  }: {
    departureLatLng: {
      lat: number | string;
      lng: number | string;
    };
    arrivalLatLng: {
      lat: number | string;
      lng: number | string;
    };
  },
) => {
  const newBody: { [key: string]: number | string } = {};
  fields.forEach((fieldName) => {
    const isLat = fieldName.includes("lat");
    const isDeparture = fieldName.includes("departure");
    if (isLat) {
      if (isDeparture) {
        newBody[fieldName] = departureLatLng.lat;
      } else {
        newBody[fieldName] = arrivalLatLng.lat;
      }
    } else {
      if (isDeparture) {
        newBody[fieldName] = departureLatLng.lng;
      } else {
        newBody[fieldName] = arrivalLatLng.lng;
      }
    }
  });
  return newBody;
};
const FlightForm = () => {
  const { t } = useTranslation();
  const { userData } = useUser();

  const { userId, flightId } = useParams();
  const { navigateWithEvent, eventId } = useEventRouting();
  const { data: attendeesData } = useQuery({
    queryKey: ["attendeesData", eventId],
    queryFn: () => getAttendees(eventId),
    select: (data) => data.data,
  });
  const [defaultCountryCode, setDefaultCountryCode] = useState();
  const form = useForm<FlightFormSchemaType>({
    defaultValues: FlightModel.default(userId!, null, userData?.flight_type!),
    resolver: zodResolver(getFlightFormSchema(t)),
    mode: "onBlur",
  });

  const [ticket, setTicket] = useState<TicketState>({
    ...INITIAL_TICKET_STATE,
  });

  const isAIFlightEnabled = useFeatureFlag(FEATURE_FLAGS.extractFlightAI);

  const [isLoading, setLoading] = useState(false);
  const prefillFlight = async () => {
    setLoading(true);
    try {
      const loadingToast = toast.loading(t("flightForm.loadingFlightDetails"));
      const { ok, data: flight } = await getFlightById(flightId, eventId);
      if (ok === false) return;

      const flightData = FlightModel.apply(flight);
      if (flight.outbound_duration_time.length) {
        let indexOfColon = flight.outbound_duration_time.indexOf(":");
        let hours = "";
        let minutes = "";
        if (indexOfColon > -1) {
          hours = flight.outbound_duration_time.substring(0, indexOfColon);
          minutes = flight.outbound_duration_time.substring(indexOfColon + 1);
        }
        flightData.outbound_duration_hours = +hours;
        flightData.outbound_duration_minutes = +minutes;
      }
      if (flight.return_duration_time.length) {
        let indexOfColon = flight.return_duration_time.indexOf(":");
        let hours = "";
        let minutes = "";
        if (indexOfColon > -1) {
          hours = flight.return_duration_time.substring(0, indexOfColon);
          minutes = flight.return_duration_time.substring(indexOfColon + 1);
        }
        flightData.return_duration_hours = +hours;
        flightData.return_duration_minutes = +minutes;
      }
      form.reset(flightData);
      if (flight.outbound_flight_pdf.length > 0) {
        setTicket({
          state: "success",
          url: flight.outbound_flight_pdf,
          isLoading: false,
          name: "ticket.pdf",
        });
        form.setValue("flight_ticket", flight.outbound_flight_pdf);
      }
      if (
        flight.user_id === userData?.id ||
        flight.user_id === userData?.hash_id
      ) {
        form.setValue("user_id", userData?.id!);
      }
      setTimeout(() => {
        toast.dismiss(loadingToast);
      }, 1000);
    } catch {
      toast.error(t("flightForm.failedLoadFlightDetails"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (flightId === "new") return;
    prefillFlight();
  }, []);
  const onSubmit = async (output: any) => {
    const data: Required<FlightFormOutput> = output;
    try {
      setLoading(true);
      const serverRequest = {};

      if (
        new Date(data.outbound_arrival_date) <
        new Date(data.outbound_departure_date)
      ) {
        openToast(t("flightForm.outboundDateError"), "error");

        return;
      }

      if (
        data.return_flight_type &&
        data.return_flight_type !== "0" &&
        new Date(data.return_arrival_date) <
        new Date(data.return_departure_date)
      ) {
        openToast(t("flightForm.returnDateError"), "error");

        return;
      }

      Object.keys(data).forEach((field) => {
        const key = field as keyof FlightFormOutput;
        if (
          key.toString().startsWith("return_") &&
          data.return_flight_type === "0"
        )
          return;

        Object.assign(serverRequest, {
          [key]: data[key],
        });
      });

      Object.assign(serverRequest, {
        companion_ids: data.companion_ids.map((id: number | string) =>
          typeof id === "number" ? id : Number(id),
        ),
      });

      if (data.companion_ids.length === 0 && !data.has_user) {
        openToast(t("flightForm.selectAttendeeError"), "error");
        setLoading(false);
        return;
      }

      if (getFlightType(data.outbound_flight_type) === "pr") {
        const outboundDepartureLatLng = await getLatLngByAddress(
          data["outbound_departure_location"],
        );

        const outboundArrivalLatLng = await getLatLngByAddress(
          data["outbound_arrival_location"],
        );

        Object.assign(serverRequest, {
          ...applyLocationsLatLngToBody(
            SERVER_OUTBOUND_LOCATION_LAT_LNG_FIELDS,
            {
              departureLatLng: outboundDepartureLatLng,
              arrivalLatLng: outboundArrivalLatLng,
            },
          ),
        });
      }

      if (
        data.return_flight_type &&
        data.return_flight_type !== "0" &&
        getFlightType(data.return_flight_type) === "pr"
      ) {
        const returnDepartureLatLng = await getLatLngByAddress(
          data["return_departure_location"],
        );

        const returnArrivalLatLng = await getLatLngByAddress(
          data["return_arrival_location"],
        );

        Object.assign(serverRequest, {
          ...applyLocationsLatLngToBody(SERVER_RETURN_LOCATION_LAT_LNG_FIELDS, {
            departureLatLng: returnDepartureLatLng,
            arrivalLatLng: returnArrivalLatLng,
          }),
        });
      }

      Object.assign(serverRequest, {
        user_id: userId,
        outbound_duration_time: `${data.outbound_duration_hours || "00"}:${data.outbound_duration_minutes || "00"
          }`,
        outbound_flight_pdf: ticket.url || data.flight_ticket || "",
      });

      if (data.return_flight_type) {
        Object.assign(serverRequest, {
          return_duration_time: `${data.return_duration_hours || "00"}:${data.return_duration_minutes || "00"
            }`,
        });
      }
      let res;
      if (flightId === "new") {
        res = await createFlight(serverRequest, eventId);
      } else {
        res = await updateFlight(flightId, serverRequest, eventId);
      }
      if (res.ok === true) {
        return navigateWithEvent(ROUTES.dashboard.base, {
          replace: true,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const goBack = usePreviousRoute(ROUTES.dashboard.base);

  useEffect(() => {
    const fetchCountryCode = async () => {
      try {
        const { data } = await getEventDefaultCountryCode(eventId);
        setDefaultCountryCode(data?.country_code);
      } catch (error) {
        console.error("Failed to fetch country code:", error);
      }
    };

    fetchCountryCode();
  }, []);

  const [extractedFlightInfo, setExtractedFlightInfo] =
    useState<FlightFieldsToExtract | null>(null);
  const { extractFlightAi, isLoading: isExtractingFlightInfo } =
    useExtractFlightAi(form, setExtractedFlightInfo);

  const temporaryFileRef = useRef<File[] | null>(null);
  return (
    <PageLayout
      title={t("flightForm.flightTicketUploadLabel")}
      hasGoBack
      goBackMethod={() => goBack(ROUTES.dashboard.base)}
    >
      <Form {...form}>
        <form
          id="flight-form"
          onSubmit={form.handleSubmit(onSubmit, (e) => {
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
            if (e.companion_ids?.message) {
              openToast(e.companion_ids?.message, "error");
            }
            openToast(t("flightForm.reviewFaultyFields"), "error");

            if (process.env.NODE_ENV === "development")
              console.log({ errors: e, values: form.getValues() });
          })}
        >
          <Stack className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 py-10 md:px-[54px] mt-5">
            <FormSection title={t("flightForm.documentsLabel")}>
              <Stack>
                {/* <div
                className="inline"
                role="button"
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    content: (
                      <Text className="text-primary-500">
                        <Text as="strong">Please Note:</Text> an integrated AI
                        will attempt to extract that data from the uploaded .pdf
                        file, please double check the extracted data after
                        completion.
                      </Text>
                    ),
                    title: "AI Data Extraction",
                  });
                }}
              >
                <span className="underline">
                  Upload Flight Ticket (your data will be extracted
                  automatically)
                </span>
              </div> */}
                <Stack className="relative">
                  <div className="flex flex-col sm:flex-row sm:gap-x-2.5 space-y-2.5 sm:space-y-s0 mt-2.5">
                    <FormField
                      name="flight_ticket"
                      render={({ field }) => (
                        <FormItem className="flex flex-col w-full">
                          <FormLabel>
                            {t("flightForm.flightTicketLabel")}
                          </FormLabel>
                          {isAIFlightEnabled ? (
                            <Stack className="flex gap-2.5">
                              <AIUploadComponent
                                uploadedFileName={
                                  ticket.state === "success"
                                    ? ticket.name || undefined
                                    : undefined
                                }
                                uploadedFileUrl={
                                  ticket.state === "success"
                                    ? ticket.url || undefined
                                    : undefined
                                }
                                onChange={async (files) => {
                                  const file = files?.[files.length - 1];

                                  setTicket({ ...INITIAL_TICKET_STATE });
                                  form.setValue("flight_ticket", "");
                                  form.clearErrors("flight_ticket");
                                  if (!file)
                                    return openToast(
                                      t("flightForm.fileRejected"),
                                      "error",
                                    );

                                  setExtractedFlightInfo(null);
                                  form.reset(
                                    FlightModel.default(
                                      userId!,
                                      null,
                                      userData?.flight_type!,
                                    ),
                                  );
                                  form.clearErrors();

                                  temporaryFileRef.current = [file!];

                                  await uploadCloudinary(
                                    temporaryFileRef.current,
                                    {
                                      allowedFileTypes: [
                                        ".pdf",
                                        ".jpg",
                                        ".jpeg",
                                        ".png",
                                      ],
                                      maxSize: 10,
                                      folderName: "flight-tickets",
                                      middleware: undefined,
                                      onUploadStart: () =>
                                        setTicket((prev) => ({
                                          ...prev,
                                          state: "initial",
                                          isLoading: true,
                                        })),
                                      onError: () => {
                                        setTicket((prev) => ({
                                          ...prev,
                                          state: "initial",
                                          isLoading: false,
                                        }));
                                        form.setValue("flight_ticket", "");
                                      },
                                      onUpload: async (
                                        data: string | any[],
                                      ) => {
                                        if (data?.length === 0) {
                                          form.setValue("flight_ticket", "");
                                          return setTicket((prev) => ({
                                            ...prev,
                                            state: "initial",
                                            isLoading: false,
                                          }));
                                        }
                                        const url = data[data.length - 1].img;

                                        setTicket((prev) => ({
                                          ...prev,
                                          url,
                                          isLoading: false,
                                        }));
                                        form.setValue("flight_ticket", url, {
                                          shouldDirty: true,
                                        });
                                        form.clearErrors("flight_ticket");
                                        await extractFlightAi(file!)
                                          .then(() => {
                                            setTicket((prev) => ({
                                              ...prev,
                                              state: "success",
                                              name: file.name,
                                            }));
                                            openToast(
                                              t("flightForm.extractionSuccess"),
                                              "success",
                                            );
                                          })
                                          .catch(() => {
                                            setTicket((prev) => ({
                                              ...prev,
                                              state: "success",
                                              name: file.name,
                                            }));
                                            openToast(
                                              t("flightForm.extractionError"),
                                              "error",
                                            );
                                          });
                                      },
                                    },
                                  );
                                }}
                                isLoading={ticket.isLoading}
                                isProcessing={isExtractingFlightInfo}
                              />
                            </Stack>
                          ) : (
                            <FileInput
                              hideSaveButton
                              formats={[".pdf", ".jpg", ".jpeg", ".png"]}
                              maxSize={10}
                              onChange={async (e) => {
                                const files = e?.target?.files;
                                const file = files?.[files.length - 1];
                                setTicket({
                                  ...INITIAL_TICKET_STATE,
                                  name: file?.name || "",
                                });
                                form.setValue("flight_ticket", "");
                                form.clearErrors("flight_ticket");
                                if (!file)
                                  return openToast(
                                    t("flightForm.fileRejected"),
                                    "error",
                                  );
                                temporaryFileRef.current = [file!];

                                await uploadCloudinary(
                                  temporaryFileRef.current,
                                  {
                                    allowedFileTypes: [
                                      ".pdf",
                                      ".jpg",
                                      ".jpeg",
                                      ".png",
                                    ],
                                    maxSize: 10,
                                    folderName: "flight-tickets",
                                    middleware: undefined,
                                    onUploadStart: () =>
                                      setTicket((prev) => ({
                                        ...prev,
                                        state: "initial",
                                        isLoading: true,
                                      })),
                                    onError: () => {
                                      setTicket((prev) => ({
                                        ...prev,
                                        state: "initial",
                                        isLoading: false,
                                      }));
                                      form.setValue("flight_ticket", "");
                                    },
                                    onUpload: async (data: string | any[]) => {
                                      if (data?.length === 0) {
                                        setTicket((prev) => ({
                                          ...prev,
                                          state: "initial",
                                          isLoading: false,
                                        }));
                                        form.setValue("flight_ticket", "");
                                        return;
                                      }
                                      const url = data[data.length - 1].img;
                                      setTicket((prev) => ({
                                        ...prev,
                                        url,
                                        state: "success",
                                        isLoading: false,
                                        name: file.name,
                                      }));
                                      form.setValue("flight_ticket", url, {
                                        shouldDirty: true,
                                      });
                                      form.clearErrors("flight_ticket");
                                    },
                                  },
                                );
                              }}
                              placeholder={t("flightForm.browsePlaceholder")}
                            />
                          )}

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Stack>
              </Stack>
            </FormSection>
            <HR />
            <FormSection
              title={t("flightForm.passengersLabel")}
              note={t("flightForm.passengersNote")}
            >
              {/* {formErrors.passengers.length > 0 && (
                <div className="relative">
                  <SpanError error={formErrors.passengers} />
                </div>
              )} */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField
                  name="has_user"
                  render={({ field }) => (
                    <FormItem>
                      <SelectGuest
                        checked={form.watch("has_user")}
                        inputId={"main_user"}
                        inputName={"main_user"}
                        imageSrc={getAvatar(userData)}
                        isDisabled={isLoading}
                        first_name={userData?.first_name ?? ""}
                        last_name={userData?.last_name ?? ""}
                        onChange={(e) => {
                          const { checked } = e.target;
                          form.setValue("has_user", checked);
                          form.clearErrors("has_user");
                        }}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="companion_ids"
                  render={({ field }) =>
                    attendeesData &&
                    attendeesData?.companions_details?.length > 0 &&
                    attendeesData?.companions_details?.map((companion: any) => (
                      <FormItem>
                        <SelectGuest
                          key={companion.id}
                          //   value={flight.companions[companion.id]}
                          inputId={companion.id}
                          inputName={companion.id}
                          imageSrc={getAvatar(companion)}
                          first_name={companion.first_name}
                          last_name={companion.last_name}
                          checked={
                            !!form.watch("companion_ids").includes(companion.id)
                          }
                          isDisabled={isLoading}
                          onChange={(e) => {
                            const { checked } = e.target;
                            const companion_ids = form.watch("companion_ids");
                            const newCompanionIds = checked
                              ? [...companion_ids, companion.id]
                              : companion_ids.filter(
                                (id) => id !== companion.id,
                              );
                            form.setValue("companion_ids", newCompanionIds);
                            form.clearErrors("companion_ids");
                          }}
                        />
                        <FormMessage />
                      </FormItem>
                    ))
                  }
                />
              </div>
            </FormSection>
            <HR />
            <FormSection
              title={t("flightForm.outboundLabel")}
              note={t("flightForm.flightNote")}
              className={classNames({
                "pointer-events-none opacity-50 animate-pulse":
                  isLoading || isExtractingFlightInfo,
              })}
            >
              <FlightInfoSection
                form={form}
                type="outbound"
                defaultCountryCode={defaultCountryCode}
                flight_type={userData?.flight_type === 1 ? "pr" : "commercial"}
                disabled={isLoading}
                extractedFlightInfo={extractedFlightInfo}
              />
            </FormSection>
            <HR />
            <FormSection
              title={t("flightForm.returnFlightLabel")}
              className={classNames({
                "pointer-events-none opacity-50 animate-pulse":
                  isLoading || isExtractingFlightInfo,
              })}
            >
              <FlightInfoSection
                form={form}
                type="return"
                defaultCountryCode={defaultCountryCode}
                flight_type={userData?.flight_type === 1 ? "pr" : "commercial"}
                disabled={isLoading}
                extractedFlightInfo={extractedFlightInfo}
              />
            </FormSection>
            <HR />
            <Stack className="gap-y-10">
              <Input
                label={t("flightForm.OtherNotesLabel")}
                type="textarea"
                value={form.watch("other_notes")}
                onChange={(e) => {
                  form.setValue("other_notes", e.target.value);
                  form.trigger("other_notes");
                }}
                disabled={isLoading}
              />

              <div className="flex gap-x-2.5 items-start mt-2.5 ">
                <div className="pt-0.5">
                  <Label
                    htmlFor="is_user_verify_all_information"
                    className="-text--component-colors-check-radio-default-text text-sm font-normal flex"
                  >
                    <Checkbox
                      type="checkbox"
                      className="me-2 min-w-5"
                      name="is_user_verify_all_information"
                      id="is_user_verify_all_information"
                      checked={form.watch("is_user_verify_all_information")}
                      onChange={(e) => {
                        const { checked } = e.target;
                        form.setValue(
                          "is_user_verify_all_information",
                          checked,
                        );
                        form.trigger("is_user_verify_all_information");
                      }}
                      disabled={isLoading}
                    />
                    {t("flightForm.flightInformationAcknowledgement")}
                  </Label>
                </div>
              </div>

              <div className="grid grid-cols-1">
                <Button
                  variant="primary"
                  type="submit"
                  form="flight-form"
                  shouldPreventDefault={false}
                  isLoading={isLoading || isExtractingFlightInfo}
                  disabled={
                    isLoading ||
                    isExtractingFlightInfo ||
                    !form.watch("is_user_verify_all_information")
                  }
                >
                  {t("flightForm.saveLabel")}
                </Button>
              </div>
            </Stack>
          </Stack>
        </form>
      </Form>
    </PageLayout>
  );
};

export default FlightForm;
